import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline';

const Resume = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });

  const [expandedExperience, setExpandedExperience] = useState(null);

  const experiences = [
    {
      id: 1,
      role: "Lead Product Manager, Growth",
      company: "Pearl",
      period: "2024 - Present",
      description: "FDA-Cleared Dental AI Detections",
      keyAccomplishments: [
        "Leading growth initiatives for dental AI platform",
        "Driving product strategy for B2B SaaS solutions",
        "Optimizing user acquisition and retention metrics"
      ],
      keyProducts: [
        {
          name: "Second Opinion®",
          description: "AI-powered dental diagnosis platform",
          link: "https://www.hellopearl.com/products/second-opinion"
        },
        {
          name: "Calibrate™",
          description: "Practice optimization and analytics tool",
          link: "https://www.hellopearl.com/products/calibrate"
        }
      ]
    },
    {
      id: 2,
      role: "Head of Product",
      company: "Hifi",
      period: "2022 - 2024",
      description: "Fixed-Rate DeFi Lending Platform",
      keyAccomplishments: [
        "Led product strategy for DeFi lending protocol",
        "Launched innovative NFT marketplace platform",
        "Managed cross-functional teams in Web3 environment"
      ],
      keyProducts: [
        {
          name: "Hifi Lending Protocol",
          description: "Decentralized lending platform",
          link: "https://hifi.finance"
        },
        {
          name: "Pooled NFT Marketplace",
          description: "Fractionalized NFT trading platform",
          link: "https://hifi.finance/pooled-nft"
        },
        {
          name: "Pawn Bots NFT Collection",
          description: "NFT collection and community",
          link: "https://www.pawnbots.com/"
        }
      ]
    },
    {
      id: 3,
      role: "Senior Product Manager",
      company: "Via",
      period: "2021 - 2022",
      description: "E-commerce Text Marketing",
      keyAccomplishments: [
        "Drove product strategy for e-commerce text marketing platform",
        "Optimized campaign creation and inbox management through data-driven insights",
        "Navigated regulatory standards to deliver compliant text marketing solutions"
      ],
      keyProducts: []
    },
    {
      id: 4,
      role: "Group Product Manager",
      company: "Weave",
      period: "2016 - 2021",
      description: "Unified SMB Communication Solutions",
      keyAccomplishments: [
        "Led product development for SMS and VoIP communication solutions",
        "Managed multiple product lines serving 25,000+ customers",
        "Drove significant revenue growth through feature innovation"
      ],
      keyProducts: [
        {
          name: "Business Texting",
          description: "HIPAA-compliant messaging platform",
          link: "https://www.getweave.com/products/business-texting"
        },
        {
          name: "Online Scheduling",
          description: "Automated appointment booking system",
          link: "https://www.getweave.com/products/online-scheduling"
        },
        {
          name: "Appointment Reminders",
          description: "Automated patient communication system",
          link: "https://www.getweave.com/products/appointment-reminders"
        }
      ]
    },
    {
      id: 5,
      role: "Earlier Roles",
      company: "Various Companies",
      period: "2012 - 2016",
      description: "Foundation years building product management expertise across multiple industries and company stages",
      keyAccomplishments: [
        "Cvent: Technical Program Manager - Event management platform",
        "Vivint: Project Manager - Home Internet account onboarding",
        "AutoPoint: Program Manager - Automotive inspection tracking technology"
      ],
      keyProducts: [
        {
          name: "Event Management Platform",
          description: "Enterprise event planning and management solutions",
          link: "https://www.cvent.com"
        },
        {
          name: "Home Internet Onboarding",
          description: "Streamlined customer onboarding experience",
          link: "https://www.vivint.com"
        },
        {
          name: "Automotive Inspection Technology",
          description: "Digital tracking and management for vehicle inspections",
          link: "https://www.autopoint.com/"
        }
      ]
    }
  ];

  const toggleExpanded = (experienceId) => {
    setExpandedExperience(expandedExperience === experienceId ? null : experienceId);
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, x: -50 },
    visible: {
      opacity: 1,
      x: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut"
      }
    }
  };

  const expandVariants = {
    hidden: { 
      opacity: 0, 
      height: 0,
      transition: {
        duration: 0.3,
        ease: "easeInOut"
      }
    },
    visible: { 
      opacity: 1, 
      height: "auto",
      transition: {
        duration: 0.3,
        ease: "easeInOut"
      }
    }
  };

  const downloadResume = () => {
    window.open('https://drive.google.com/file/d/1j5DvcMOioSJhUdCTC88-XvI3DhNp0T5x/view?usp=sharing', '_blank');
  };

  return (
    <section id="resume" className="py-24 bg-white">
      <div className="container-max section-padding">
        <motion.div
          ref={ref}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
          variants={containerVariants}
        >
          <motion.div className="text-center mb-16" variants={itemVariants}>
            <h2 className="text-4xl md:text-5xl font-bold text-text-main mb-6">
              Work Experience
            </h2>
            <p className="text-xl text-secondary-accent max-w-3xl mx-auto leading-relaxed mb-8">
              My professional journey in product leadership
            </p>
            
            <motion.button
              onClick={downloadResume}
              className="inline-flex items-center px-8 py-4 bg-primary-accent text-white rounded-full font-semibold text-lg hover:bg-primary-accent/80 transition-colors duration-200"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              transition={{ type: "spring", stiffness: 400, damping: 10 }}
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              Download Resume
            </motion.button>
          </motion.div>

          <div className="max-w-4xl mx-auto">
            <motion.div variants={containerVariants}>
              {experiences.map((exp, index) => (
                <motion.div
                  key={exp.id}
                  className="bg-subtle-accent rounded-2xl overflow-hidden shadow-sm hover:shadow-lg transition-all duration-300 mb-12 last:mb-0"
                  variants={itemVariants}
                >
                  <div 
                    className="p-8 cursor-pointer"
                    onClick={() => toggleExpanded(exp.id)}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4">
                          <h3 className="text-xl font-bold text-text-main">
                            {exp.role}
                          </h3>
                          <span className="text-sm font-medium text-primary-accent bg-white px-3 py-1 rounded-full mt-2 sm:mt-0 self-start">
                            {exp.period}
                          </span>
                        </div>
                        
                        <h4 className="text-lg font-semibold text-text-main mb-3">
                          {exp.company}
                        </h4>
                        
                        <p className="text-secondary-accent leading-relaxed">
                          {exp.description}
                        </p>
                      </div>
                      
                      <div className="ml-4 flex-shrink-0">
                        {expandedExperience === exp.id ? (
                          <ChevronUpIcon className="w-6 h-6 text-secondary-accent" />
                        ) : (
                          <ChevronDownIcon className="w-6 h-6 text-secondary-accent" />
                        )}
                      </div>
                    </div>
                  </div>

                  <AnimatePresence>
                    {expandedExperience === exp.id && (
                      <motion.div
                        initial="hidden"
                        animate="visible"
                        exit="hidden"
                        variants={expandVariants}
                        className="overflow-hidden"
                      >
                        <div className="px-8 pb-8 border-t border-white/50">
                          <div className={`grid gap-8 pt-6 ${exp.keyProducts.length > 0 ? 'md:grid-cols-2' : 'md:grid-cols-1'}`}>
                            {/* Key Accomplishments */}
                            <div>
                              <h4 className="text-lg font-bold text-text-main mb-4">
                                Key Accomplishments
                              </h4>
                              <ul className="space-y-3">
                                {exp.keyAccomplishments.map((accomplishment, index) => (
                                  <li key={index} className="flex items-start gap-3">
                                    <div className="w-2 h-2 bg-primary-accent rounded-full mt-2 flex-shrink-0"></div>
                                    <span className="text-secondary-accent leading-relaxed">
                                      {accomplishment}
                                    </span>
                                  </li>
                                ))}
                              </ul>
                            </div>

                            {/* Key Products & Features - Only show if there are products */}
                            {exp.keyProducts.length > 0 && (
                              <div>
                                <h4 className="text-lg font-bold text-text-main mb-4">
                                  Key Products & Features
                                </h4>
                                <div className="space-y-4">
                                  {exp.keyProducts.map((product, index) => (
                                    <div key={index} className="group">
                                      <a 
                                        href={product.link} 
                                        target="_blank" 
                                        rel="noopener noreferrer"
                                        className="block p-4 bg-white rounded-lg hover:bg-gray-50 transition-colors duration-200"
                                        onClick={(e) => e.stopPropagation()}
                                      >
                                        <h5 className="font-semibold text-text-main group-hover:text-primary-accent transition-colors duration-200 mb-1">
                                          {product.name} →
                                        </h5>
                                        <p className="text-sm text-secondary-accent leading-relaxed">
                                          {product.description}
                                        </p>
                                      </a>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Resume; 